import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { clientService } from '@services/clientService';
import type { RootState } from '@store/index';
import type { ClientAggregate } from '@/types';
import { CACHE_DURATION } from '../utils/cache';

interface ClientState {
  clients: ClientAggregate[];
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
  lastFetched: number | null;
}

const initialState: ClientState = {
  clients: [],
  isLoading: false,
  isSaving: false,
  error: null,
  lastFetched: null,
};

export const fetchClients = createAsyncThunk(
  'clients/fetchAll',
  async (options: { force?: boolean } | undefined, { getState, rejectWithValue }) => {
    const state = (getState() as RootState).clients;
    const force = options?.force ?? false;

    if (!force && state.lastFetched && Date.now() - state.lastFetched < CACHE_DURATION) {
      return null;
    }

    try {
      const clients = await clientService.getClients();
      return clients;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch clients');
    }
  }
);

export const syncClients = createAsyncThunk('clients/sync', async (_, { rejectWithValue }) => {
  try {
    const clients = await clientService.syncClients();
    return clients;
  } catch (error) {
    return rejectWithValue(error instanceof Error ? error.message : 'Failed to sync clients');
  }
});

export const mergeClients = createAsyncThunk(
  'clients/merge',
  async (
    payload: { primaryClientId: string; secondaryClientId: string },
    { rejectWithValue, dispatch }
  ) => {
    try {
      await clientService.mergeClients(payload.primaryClientId, payload.secondaryClientId);
      await dispatch(fetchClients({ force: true }));
      return true;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to merge clients');
    }
  }
);

export const splitClientContact = createAsyncThunk(
  'clients/splitContact',
  async (payload: { clientId: string; contactId: string }, { rejectWithValue, dispatch }) => {
    try {
      await clientService.splitClientContact(payload.clientId, payload.contactId);
      await dispatch(fetchClients({ force: true }));
      return true;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to split contact from client'
      );
    }
  }
);

const clientSlice = createSlice({
  name: 'clients',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fetchClients.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchClients.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload) {
          state.clients = action.payload;
          state.lastFetched = Date.now();
        }
      })
      .addCase(fetchClients.rejected, (state, action) => {
        state.isLoading = false;
        state.error = (action.payload as string) ?? 'Failed to fetch clients';
      })
      .addCase(syncClients.pending, state => {
        state.isSaving = true;
        state.error = null;
      })
      .addCase(syncClients.fulfilled, (state, action) => {
        state.isSaving = false;
        state.clients = action.payload;
        state.lastFetched = Date.now();
      })
      .addCase(syncClients.rejected, (state, action) => {
        state.isSaving = false;
        state.error = (action.payload as string) ?? 'Failed to sync clients';
      })
      .addCase(mergeClients.pending, state => {
        state.isSaving = true;
        state.error = null;
      })
      .addCase(mergeClients.fulfilled, state => {
        state.isSaving = false;
      })
      .addCase(mergeClients.rejected, (state, action) => {
        state.isSaving = false;
        state.error = (action.payload as string) ?? 'Failed to merge clients';
      })
      .addCase(splitClientContact.pending, state => {
        state.isSaving = true;
        state.error = null;
      })
      .addCase(splitClientContact.fulfilled, state => {
        state.isSaving = false;
      })
      .addCase(splitClientContact.rejected, (state, action) => {
        state.isSaving = false;
        state.error = (action.payload as string) ?? 'Failed to split client contact';
      });
  },
});

export const selectClients = (state: RootState) => state.clients.clients;
export default clientSlice.reducer;
